import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ClipboardList } from 'lucide-react';
import type { Intervention } from '../../generated/api/types.gen.js';
import { listInterventionsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Badge,
  Button,
  Callout,
  DataTable,
  EmptyState,
  FilterPills,
  MultiSelectDropdown,
  SearchBar,
  Toolbar,
} from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/ListPage.js';
import { PAGE_HELP } from '../../help/index.js';
import { OUTCOMES, SEVERITY_VARIANT, SOURCE_TYPES, STATUSES, STATUS_VARIANT } from './action-request-labels.js';
import { formatDateTime, humanize } from './admin-format.js';

const STATUS_PILLS = [
  { id: 'all', label: 'All' },
  ...STATUSES.map((status) => ({ id: status, label: humanize(status) })),
];
const SOURCE_OPTIONS = SOURCE_TYPES.map((value) => ({ value, label: humanize(value) }));
const OUTCOME_OPTIONS = OUTCOMES.map((value) => ({ value, label: humanize(value) }));

export const ActionRequestsPage: React.FC = () => {
  const help = PAGE_HELP['action-requests'];
  const navigate = useNavigate();
  const [statusId, setStatusId] = useState('open');
  const [sources, setSources] = useState<Intervention['sourceType'][]>([]);
  const [outcomes, setOutcomes] = useState<Intervention['requestedOutcome'][]>([]);
  const [search, setSearch] = useState('');

  const status = STATUSES.find((candidate) => candidate === statusId);
  const { data, isPending, isError, refetch } = useQuery(listInterventionsOptions({ query: { status, limit: 100 } }));

  const needle = search.trim().toLowerCase();
  const rows = (data?.data ?? []).filter(
    (request) =>
      (sources.length === 0 || sources.includes(request.sourceType)) &&
      (outcomes.length === 0 || outcomes.includes(request.requestedOutcome)) &&
      (!needle ||
        [request.title, request.reason, request.sourceName, request.type].some((value) =>
          value.toLowerCase().includes(needle),
        )),
  );

  const columns: DataTableColumn<Intervention>[] = [
    {
      key: 'request',
      header: 'Request',
      render: (request) => (
        <div className="min-w-0 max-w-md">
          <Link
            to={`/action-requests/${request.id}`}
            className="block truncate text-sm font-medium text-heading hover:text-accent-text"
            onClick={(e) => e.stopPropagation()}
          >
            {request.title}
          </Link>
          <span className="block text-xs text-muted">{humanize(request.type)}</span>
        </div>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (request) => <Badge variant={SEVERITY_VARIANT[request.severity]}>{humanize(request.severity)}</Badge>,
    },
    {
      key: 'source',
      header: 'Source',
      render: (request) => (
        <div>
          <span className="block text-sm text-heading">{request.sourceName}</span>
          <span className="block text-xs text-muted">{humanize(request.sourceType)}</span>
        </div>
      ),
    },
    {
      key: 'outcome',
      header: 'Requested outcome',
      render: (request) => (
        <span className="text-sm text-muted">
          {request.requestedOutcome ? humanize(request.requestedOutcome) : 'Triage'}
        </span>
      ),
    },
    {
      key: 'assigned',
      header: 'Assigned to',
      render: (request) => <span className="text-sm text-muted">{request.assignedToName ?? 'Unassigned'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (request) => (
        <Badge variant={STATUS_VARIANT[request.status]} dot>
          {humanize(request.status)}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Opened',
      sortable: true,
      sortValue: (request) => request.createdAt,
      render: (request) => (
        <span className="whitespace-nowrap text-sm text-muted">{formatDateTime(request.createdAt)}</span>
      ),
    },
  ];

  return (
    <ListPage
      title="Action requests"
      subtitle="Intervention, repair, decision and worker-availability requests waiting to be routed."
      helpContent={help}
      alert={
        isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load action requests"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search title, reason or source…" />}
          pills={<FilterPills aria-label="Status" options={STATUS_PILLS} value={statusId} onChange={setStatusId} />}
          right={
            <>
              <MultiSelectDropdown label="Source" options={SOURCE_OPTIONS} values={sources} onChange={setSources} />
              <MultiSelectDropdown
                label="Outcome"
                options={OUTCOME_OPTIONS}
                values={outcomes.filter((value): value is NonNullable<typeof value> => value !== null)}
                onChange={setOutcomes}
              />
            </>
          }
        />
      }
    >
      {!isError && (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(request) => request.id}
          onRowClick={(request) => navigate(`/action-requests/${request.id}`)}
          loading={isPending}
          aria-label="Action requests"
          empty={
            <EmptyState
              icon={ClipboardList}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
            />
          }
        />
      )}
    </ListPage>
  );
};

export default ActionRequestsPage;
